import { LegalLayout, LegalSection } from "@/components/LegalLayout";

export default function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" updated="June 1, 2026">
      <p className="text-slate-600 leading-relaxed text-[15px]">
        This Privacy Policy explains how <strong>ReachMeNow</strong> ("ReachMeNow", "we", "us", or "our")
        collects, uses, and protects your information when you use our contact-sharing service, website,
        QR tags, and related products (collectively, the "Service"). By using the Service, you agree to the
        practices described in this policy.
      </p>

      <LegalSection heading="1. What We Collect">
        <p>We collect the following types of information:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Account information:</strong> your full name, email address, and country when you register.</li>
          <li><strong>Messages:</strong> when someone sends a message through your ReachMeNow QR code, we collect the <strong>message content</strong>, the <strong>sender's IP address</strong>, and a <strong>timestamp</strong>, along with the ongoing conversation history between you and the visitor.</li>
          <li><strong>Usage data:</strong> QR code scans, timestamps, device and browser type, and general analytics.</li>
          <li><strong>Communications preferences:</strong> your consent to receive updates and your interest in our crowdfunding campaign.</li>
        </ul>
        <p>Importantly, ReachMeNow is designed so that visitors who contact you <strong>never see your phone number or email address</strong>.</p>
      </LegalSection>

      <LegalSection heading="2. How We Use Your Information">
        <ul className="list-disc pl-5 space-y-1">
          <li>To create and operate your account and QR codes.</li>
          <li>To deliver messages between you and the people who contact you in real time.</li>
          <li>To send you product news, updates, beta invitations, and promotional emails you have consented to.</li>
          <li>To improve, secure, and analyze the Service.</li>
          <li>To comply with legal obligations.</li>
        </ul>
      </LegalSection>

      <LegalSection heading="3. How We Share Information">
        <p>We do <strong>not</strong> sell your personal information. We may share information only:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>With service providers who help us operate the Service (e.g. hosting, email, analytics), under confidentiality obligations.</li>
          <li>When required by law, regulation, or valid legal process.</li>
          <li>To protect the rights, safety, and security of ReachMeNow, our users, or the public.</li>
          <li>In connection with a merger, acquisition, or sale of assets, with notice to you.</li>
        </ul>
      </LegalSection>

      <LegalSection heading="4. Data Retention">
        <p>We retain your information for as long as your account is active or as needed to provide the Service. You may request deletion of your account and associated data at any time by contacting us.</p>
      </LegalSection>

      <LegalSection heading="5. Your Rights & Choices">
        <ul className="list-disc pl-5 space-y-1">
          <li>You can access, update, or delete your personal information.</li>
          <li>You can unsubscribe from marketing emails at any time using the link in each email.</li>
          <li>Depending on your location, you may have additional rights under laws such as GDPR or CCPA.</li>
        </ul>
      </LegalSection>

      <LegalSection heading="6. Security">
        <p>We use reasonable technical and organizational measures to protect your information. However, no method of transmission or storage is 100% secure, and we cannot guarantee absolute security.</p>
      </LegalSection>

      <LegalSection heading="7. Children's Privacy">
        <p>The Service is not directed to children under 13 (or the applicable age in your jurisdiction), and we do not knowingly collect personal information from them.</p>
      </LegalSection>

      <LegalSection heading="8. Changes to This Policy">
        <p>We may update this Privacy Policy from time to time. We will post the updated version with a new "Last updated" date, and material changes may be communicated to you directly.</p>
      </LegalSection>

      <LegalSection heading="9. Contact Us">
        <p>If you have questions about this Privacy Policy, contact ReachMeNow at <a className="text-brand-blue hover:underline" href="mailto:support@reachmenow.ca">support@reachmenow.ca</a>.</p>
      </LegalSection>
    </LegalLayout>
  );
}
